import React, { FC, useRef } from "react"
import { useTranslation } from "react-i18next"
import Container from "@/components/Container"
import Box from "@/components/Box"
import Typography from "@/components/Typography"
import SimpleFadeTextAnimation from "@/components/SimpleFadeTextAnimation"
import RevealTextAnimation from "@/components/RevealTextAnimation"
import { m } from "framer-motion"
import useScrollProgress from "@/components/hooks/useScrollProgress"

const FeaturesSection: FC = () => {
  const { t } = useTranslation("security")
  const wrapperRef = useRef()
  const { scrollYProgress } = useScrollProgress({ wrapperRef, threshold: 0 })

  return (
    <Box
      bg="backgroundSecurityWhite"
      color="primaryDeepBlack"
      overflow="hidden"
      ref={wrapperRef}
      pt={[0, 10, 16]}
      pb={[10, 10, 16]}
    >
      <Container>
        <Box
          height={[352, 0, 600]}
          width={336}
          position="relative"
          ml="auto"
          mr={[-100, -150, 0]}
          customCss={{ transform: ["scale(0.4)", "scale(0.4)", "scale(1)"] }}
        >
          <m.div
            animate={{ x: 150 * scrollYProgress, y: 180 * scrollYProgress }}
            transition={{
              duration: 0,
              ease: "linear",
              bounce: 0,
            }}
          >
            <Box position="absolute" width={336}>
              <img
                src={`${process.env.ASSETS_BASE_URL}/security-page/features-section/layer_4.webp`}
                width="100%"
                alt="Layer 4"
                loading="lazy"
              />
            </Box>
          </m.div>

          <Box position="absolute" width={336} right={0} top={0}>
            <img
              src={`${process.env.ASSETS_BASE_URL}/security-page/features-section/layer_3.webp`}
              alt="Layer 3"
              width="100%"
              loading="lazy"
            />
          </Box>

          <m.div
            animate={{ x: -180 * scrollYProgress, y: -156 * scrollYProgress }}
            transition={{
              duration: 0,
              ease: "linear",
              bounce: 0,
            }}
          >
            <Box position="absolute" width={336}>
              <img
                src={`${process.env.ASSETS_BASE_URL}/security-page/features-section/layer_2.webp`}
                alt="Layer 2"
                width="100%"
                loading="lazy"
              />
            </Box>
          </m.div>

          <m.div
            animate={{ x: -400 * scrollYProgress, y: -276 * scrollYProgress }}
            transition={{
              duration: 0,
              ease: "linear",
              bounce: 0,
            }}
          >
            <Box position="absolute" width={336}>
              <img
                src={`${process.env.ASSETS_BASE_URL}/security-page/features-section/layer_1.webp`}
                alt="Layer 1"
                width="100%"
                loading="lazy"
              />
            </Box>
          </m.div>
        </Box>
      </Container>
      <Container>
        <SimpleFadeTextAnimation>
          <Box width={["100%", 542, 805]}>
            <Typography
              variant="h4"
              color="primaryBrightBlueLightBg"
              mb={[2, 1, 2]}
            >
              {t("section_three.eyebrow")}
            </Typography>
          </Box>
        </SimpleFadeTextAnimation>
        <Box width={["100%", 416, 898]}>
          <Typography variant="h2" mb={[12, 8, 16]}>
            <RevealTextAnimation
              text={t("section_three.h2_new") as string}
            ></RevealTextAnimation>
          </Typography>
        </Box>
        <SimpleFadeTextAnimation>
          <Box>
            <Box width={["100%", 417, 804]} mb={[1, 0.5, 1]}>
              <Typography variant="h5">
                {t("section_three.contents_0_title")}
              </Typography>
            </Box>
            <Box width={["100%", 417, 804]} mb={[7, 4, 8]}>
              <Typography variant="bodyCopy" color="#565F76">
                {t("section_three.contents_0_paragraph")}
              </Typography>
            </Box>
          </Box>
        </SimpleFadeTextAnimation>

        <SimpleFadeTextAnimation>
          <Box>
            <Box width={["100%", 417, 804]} mb={[1, 0.5, 1]}>
              <Typography variant="h5">
                {t("section_three.contents_1_title")}
              </Typography>
            </Box>
            <Box width={["100%", 417, 804]} mb={[7, 4, 8]}>
              <Typography variant="bodyCopy" color="#565F76">
                {t("section_three.contents_1_paragraph")}
              </Typography>
            </Box>
          </Box>
        </SimpleFadeTextAnimation>

        <SimpleFadeTextAnimation>
          <Box>
            <Box width={["100%", 417, 804]} mb={[1, 0.5, 1]}>
              <Typography variant="h5">
                {t("section_three.contents_2_title")}
              </Typography>
            </Box>
            <Box width={["100%", 417, 804]} mb={[7, 4, 8]}>
              <Typography variant="bodyCopy" color="#565F76">
                {t("section_three.contents_2_paragraph")}
              </Typography>
            </Box>
          </Box>
        </SimpleFadeTextAnimation>

        <SimpleFadeTextAnimation>
          <Box>
            <Box width={["100%", 417, 804]} mb={[1, 0.5, 1]}>
              <Typography variant="h5">
                {t("section_three.contents_3_title")}
              </Typography>
            </Box>
            <Box width={["100%", 417, 804]} mb={[7, 4, 8]}>
              <Typography variant="bodyCopy" color="#565F76">
                {t("section_three.contents_3_paragraph")}
              </Typography>
            </Box>
          </Box>
        </SimpleFadeTextAnimation>
      </Container>
    </Box>
  )
}

export default FeaturesSection
